(function () {
  'use strict';

  var root = document.documentElement;

  /* ---------------------------------------------------------------------
   * Header scroll state
   * ------------------------------------------------------------------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var onScroll = function () {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ---------------------------------------------------------------------
   * Drawers (cart + mobile nav)
   * ------------------------------------------------------------------- */
  var backdrop = document.querySelector('[data-drawer-backdrop]');
  var openDrawers = [];

  function openDrawer(id) {
    var el = document.getElementById(id);
    if (!el) return;
    el.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    openDrawers.push(el);
    document.body.style.overflow = 'hidden';
    var focusable = el.querySelector('button, a, input');
    if (focusable) focusable.focus({ preventScroll: true });
  }

  function closeDrawers() {
    openDrawers.forEach(function (el) { el.classList.remove('is-open'); });
    openDrawers = [];
    if (backdrop) backdrop.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  document.addEventListener('click', function (e) {
    var openTrigger = e.target.closest('[data-drawer-open]');
    if (openTrigger) {
      openDrawer(openTrigger.getAttribute('data-drawer-open'));
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target === backdrop) {
      closeDrawers();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeDrawers();
  });

  /* ---------------------------------------------------------------------
   * Scroll reveal
   * ------------------------------------------------------------------- */
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var revealEls = document.querySelectorAll('.reveal');

  var runCountUp = function (target, animate) {
    var raw = target.getAttribute('data-count-up') || '';
    var match = raw.match(/^(\d+)(.*)$/);
    if (!match) return;
    var end = parseInt(match[1], 10);
    var suffix = match[2] || '';
    if (!animate) {
      target.textContent = end + suffix;
      return;
    }
    var duration = 1200;
    var start = null;
    var step = function (timestamp) {
      if (start === null) start = timestamp;
      var progress = Math.min((timestamp - start) / duration, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      target.textContent = Math.round(eased * end) + suffix;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  if (revealEls.length) {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      revealEls.forEach(function (el) {
        el.classList.add('is-visible');
        var counter = el.querySelector('[data-count-up]');
        if (counter) runCountUp(counter, false);
      });
    } else {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            var counter = entry.target.querySelector('[data-count-up]');
            if (counter) runCountUp(counter, true);
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
      revealEls.forEach(function (el) { io.observe(el); });
    }
  }

  /* ---------------------------------------------------------------------
   * Hero image entrance
   * ------------------------------------------------------------------- */
  var hero = document.querySelector('.hero');
  if (hero) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { hero.classList.add('is-loaded'); });
    });
  }

  /* ---------------------------------------------------------------------
   * Scroll-expand hero (video grows from a small frame to full-bleed as
   * the tall wrapper scrolls past; driven by natural scroll position, not
   * by intercepting wheel/touch events, so it never fights the browser's
   * native scrolling on trackpads or iOS Safari. The video itself never
   * autoplays — it's paused and scrubbed frame-by-frame to match scroll
   * position, advancing on scroll down and reversing on scroll up.)
   * ------------------------------------------------------------------- */
  var scrollHero = document.querySelector('.scroll-hero');
  if (scrollHero && !reduceMotion) {
    var pin = scrollHero.querySelector('.scroll-hero-pin');
    var frame = scrollHero.querySelector('.scroll-hero-frame');
    var video = scrollHero.querySelector('video');
    var ticking = false;

    if (video) {
      video.removeAttribute('autoplay');
      // iOS Safari won't render programmatic currentTime seeks on a video
      // that has never played — it hasn't decoded any frame data yet, so
      // scrubbing looks frozen on a blank/poster frame. A muted play()
      // immediately followed by pause() "primes" the decoder (allowed
      // without a user gesture since the video is muted) without the
      // video actually visibly playing.
      var primePlay = video.play();
      if (primePlay && typeof primePlay.then === 'function') {
        primePlay.then(function () { video.pause(); }).catch(function () { video.pause(); });
      } else {
        video.pause();
      }
    }

    var syncFrameRatio = function () {
      if (video && video.videoWidth && video.videoHeight) {
        frame.style.aspectRatio = video.videoWidth + ' / ' + video.videoHeight;
      }
    };
    syncFrameRatio();
    if (video && video.readyState < 1) {
      video.addEventListener('loadedmetadata', syncFrameRatio, { once: true });
    }

    var updateScrollHero = function () {
      ticking = false;
      var rect = scrollHero.getBoundingClientRect();
      var scrollable = rect.height - window.innerHeight;
      var progress = scrollable > 0 ? -rect.top / scrollable : 0;
      progress = Math.max(0, Math.min(1, progress));

      var isMobile = window.innerWidth < 768;
      var w = 320 + progress * (isMobile ? 620 : 1200);
      var textOpacity = Math.max(0, 1 - progress * 1.6);

      pin.style.setProperty('--hero-w', w + 'px');
      pin.style.setProperty('--hero-bg-opacity', progress.toFixed(3));
      pin.style.setProperty('--hero-text-opacity', textOpacity.toFixed(3));

      if (video && video.duration && isFinite(video.duration)) {
        video.currentTime = progress * video.duration;
      }
    };

    var onScrollHero = function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(updateScrollHero);
      }
    };

    updateScrollHero();
    window.addEventListener('scroll', onScrollHero, { passive: true });
    window.addEventListener('resize', onScrollHero, { passive: true });

    if (video && video.readyState < 1) {
      video.addEventListener('loadedmetadata', updateScrollHero, { once: true });
    }
  }

  /* ---------------------------------------------------------------------
   * Accordion
   * ------------------------------------------------------------------- */
  document.querySelectorAll('.accordion-trigger').forEach(function (trigger) {
    var item = trigger.closest('.accordion-item');
    var panel = item.querySelector('.accordion-panel');
    trigger.addEventListener('click', function () {
      var isOpen = item.classList.contains('is-open');
      item.closest('[data-accordion]') &&
        item.closest('[data-accordion]').querySelectorAll('.accordion-item.is-open').forEach(function (openItem) {
          if (openItem !== item) {
            openItem.classList.remove('is-open');
            openItem.querySelector('.accordion-panel').style.maxHeight = null;
          }
        });
      item.classList.toggle('is-open', !isOpen);
      panel.style.maxHeight = !isOpen ? panel.scrollHeight + 'px' : null;
      trigger.setAttribute('aria-expanded', String(!isOpen));
    });
  });

  /* ---------------------------------------------------------------------
   * Form submit feedback (e.g. Custom Orders) — the form still submits
   * natively (page reload), this just acknowledges the click immediately
   * instead of leaving the button looking inert for a beat.
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-submit-feedback]').forEach(function (btn) {
    var form = btn.closest('form');
    if (!form) return;
    form.addEventListener('submit', function () {
      btn.disabled = true;
      btn.textContent = btn.getAttribute('data-submit-label') || 'Sending…';
      btn.classList.add('is-submitting');
    });
  });

  /* ---------------------------------------------------------------------
   * Process steps — soft glow follows the cursor within each row via
   * CSS custom properties, instead of a fixed connecting line.
   * ------------------------------------------------------------------- */
  if (!reduceMotion) {
    document.querySelectorAll('.process-step').forEach(function (step) {
      step.addEventListener('mousemove', function (e) {
        var rect = step.getBoundingClientRect();
        step.style.setProperty('--spot-x', ((e.clientX - rect.left) / rect.width) * 100 + '%');
        step.style.setProperty('--spot-y', ((e.clientY - rect.top) / rect.height) * 100 + '%');
      });
    });
  }

  /* ---------------------------------------------------------------------
   * Product card spotlight — hovering a card dims the rest of the page
   * via one shared fixed overlay (pointer-events: none, so it never
   * blocks clicks underneath); the card itself pops above the overlay
   * via z-index while its image enlarges/brightens in the CSS above.
   * ------------------------------------------------------------------- */
  var productCards = document.querySelectorAll('.product-card');
  if (productCards.length && !reduceMotion) {
    var spotlightOverlay = document.createElement('div');
    spotlightOverlay.className = 'spotlight-overlay';
    spotlightOverlay.setAttribute('aria-hidden', 'true');
    document.body.appendChild(spotlightOverlay);

    productCards.forEach(function (card) {
      card.addEventListener('mouseenter', function () { spotlightOverlay.classList.add('is-active'); });
      card.addEventListener('mouseleave', function () { spotlightOverlay.classList.remove('is-active'); });
    });
  }

  /* ---------------------------------------------------------------------
   * Product gallery
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-gallery]').forEach(function (gallery) {
    var main = gallery.querySelector('[data-gallery-main]');
    gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        gallery.querySelectorAll('[data-gallery-thumb]').forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
        if (main) main.src = thumb.getAttribute('data-full') || thumb.src;
      });
    });
  });

  /* ---------------------------------------------------------------------
   * Variant / quantity stepper (product form, not cart)
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-qty-stepper]').forEach(function (stepper) {
    var input = stepper.querySelector('input');
    stepper.querySelector('[data-step-down]').addEventListener('click', function () {
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1);
    });
    stepper.querySelector('[data-step-up]').addEventListener('click', function () {
      input.value = (parseInt(input.value, 10) || 1) + 1;
    });
  });

  /* ---------------------------------------------------------------------
   * Cart: AJAX add / update / remove
   * ------------------------------------------------------------------- */
  var cartItemsEl = document.querySelector('[data-cart-items]');
  var cartCountEls = document.querySelectorAll('[data-cart-count]');

  function refreshCartDrawer() {
    var currentDrawer = document.getElementById('cart-drawer');
    var currentBody = currentDrawer && currentDrawer.querySelector('.drawer-body');
    if (currentBody) currentBody.classList.add('is-updating');

    fetch('/?section_id=cart-drawer')
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newDrawer = doc.getElementById('cart-drawer');
        if (!newDrawer || !currentDrawer) return;

        var oldSubtotalEl = currentDrawer.querySelector('[data-cart-subtotal]');
        var oldSubtotal = oldSubtotalEl ? oldSubtotalEl.textContent : null;

        var newBody = newDrawer.querySelector('.drawer-body');
        var newFoot = newDrawer.querySelector('.drawer-foot');
        var newHeadLabel = newDrawer.querySelector('.drawer-head .logo');
        var currentFoot = currentDrawer.querySelector('.drawer-foot');
        var currentHeadLabel = currentDrawer.querySelector('.drawer-head .logo');

        // Update in place (rather than replacing the whole drawer element)
        // so the open/close transform and event listeners never reset.
        if (currentBody && newBody) currentBody.innerHTML = newBody.innerHTML;
        if (currentHeadLabel && newHeadLabel) currentHeadLabel.textContent = newHeadLabel.textContent;
        if (newFoot && currentFoot) currentFoot.innerHTML = newFoot.innerHTML;
        else if (newFoot && !currentFoot) currentDrawer.appendChild(newFoot);
        else if (!newFoot && currentFoot) currentFoot.remove();

        if (currentBody) currentBody.classList.remove('is-updating');

        var newSubtotalEl = currentDrawer.querySelector('[data-cart-subtotal]');
        if (newSubtotalEl && oldSubtotal && newSubtotalEl.textContent !== oldSubtotal) {
          newSubtotalEl.classList.add('is-pulsing');
          setTimeout(function () { newSubtotalEl.classList.remove('is-pulsing'); }, 700);
        }
      })
      .catch(function () {
        if (currentBody) currentBody.classList.remove('is-updating');
      });
  }

  function updateCartCount(count) {
    cartCountEls.forEach(function (el) { el.textContent = count; });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    var formData = new FormData(form);
    fetch('/cart/add.js', { method: 'POST', body: formData, headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        refreshCartDrawer();
        openDrawer('cart-drawer');
      })
      .catch(function () {
        openDrawer('cart-drawer');
      });
  });

  if (cartItemsEl) {
    cartItemsEl.addEventListener('click', function (e) {
      var line = e.target.closest('[data-cart-line]');
      if (!line) return;
      var key = line.getAttribute('data-key');
      var input = line.querySelector('[data-qty-input]');

      if (e.target.closest('[data-qty-increase]')) {
        input.value = parseInt(input.value, 10) + 1;
        changeLine(key, input.value);
      } else if (e.target.closest('[data-qty-decrease]')) {
        input.value = Math.max(0, parseInt(input.value, 10) - 1);
        changeLine(key, input.value);
      } else if (e.target.closest('[data-cart-remove]')) {
        line.style.maxHeight = line.scrollHeight + 'px';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { line.classList.add('is-removing'); });
        });
        changeLine(key, 0);
      }
    });
  }

  function changeLine(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        refreshCartDrawer();
      })
      .catch(function () { /* network unavailable in static preview */ });
  }
})();
